import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const menuItems = [
    { label: t('incorporation'), href: '/stiftelse-af-selskaber' },
    { label: t('properties'), href: '/koeb-og-salg-af-ejendomme' },
    { label: t('companies'), href: '/koeb-og-salg-af-virksomheder-i-ind-og-udland' },
    { label: t('investments'), href: '/investeringer-i-virksomheder' },
    { label: t('contact'), href: '/kontakt-os' },
  ];

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/' || location.pathname === '/index';
    }
    return location.pathname === href;
  };

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Row 1: Logo to left, Flags to right (and burger menu on mobile) */}
        <div className="flex justify-between items-center h-20 min-[1200px]:h-16">
          {/* Logo */}
          <Link to="/" className="flex flex-col justify-center cursor-pointer group w-fit">
            <span className="font-serif text-xl font-bold text-brand-navy tracking-tight group-hover:text-brand-gold transition-colors whitespace-nowrap leading-none">
              NNC Revision
            </span>
            <span className="font-sans text-xs font-bold text-brand-gold tracking-wider group-hover:text-brand-navy transition-colors mt-0.5">
              LTD
            </span>
          </Link>

          {/* Flags & Burger Menu Trigger */}
          <div className="flex items-center gap-4">
            {/* Flags */}
            <div className="flex items-center gap-2 min-[1200px]:border-none border-l border-slate-200 pl-4 min-[1200px]:pl-0 h-6">
              <button
                onClick={() => setLanguage('da')}
                className={`transition-all duration-200 focus:outline-none cursor-pointer ${
                  language === 'da'
                    ? 'opacity-100 scale-105'
                    : 'opacity-50 hover:opacity-100 hover:scale-105'
                }`}
                title="Dansk"
                aria-label="Skift til dansk"
              >
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=40,quality=80,format=auto/assets/894969b8-3576-4146-b673-b894927d1314/dk.jpg"
                  alt="Dansk"
                  className="w-5 h-3.5 object-cover rounded-sm shadow-xs"
                />
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`transition-all duration-200 focus:outline-none cursor-pointer ${
                  language === 'en'
                    ? 'opacity-100 scale-105'
                    : 'opacity-50 hover:opacity-100 hover:scale-105'
                }`}
                title="English"
                aria-label="Switch to English"
              >
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=40,quality=80,format=auto/assets/894969b8-3576-4146-b673-b894927d1314/en.jpg"
                  alt="English"
                  className="w-5 h-3.5 object-cover rounded-sm shadow-xs"
                />
              </button>
            </div>

            {/* Burger Menu Button (Visible on screens < 1200px) */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="min-[1200px]:hidden p-2 text-slate-600 hover:text-brand-navy focus:outline-none cursor-pointer"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Row 2: Menu links (Visible on screens >= 1200px) */}
        <div className="hidden min-[1200px]:flex justify-center border-t border-slate-100 py-3">
          <nav className="flex items-center justify-center space-x-2">
            {menuItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-4 py-1.5 text-sm font-medium transition-all duration-200 cursor-pointer border-b-2 ${
                  isActive(item.href)
                    ? 'text-brand-gold border-brand-gold font-semibold'
                    : 'text-slate-600 border-transparent hover:text-brand-navy hover:border-slate-300'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Navigation Drawer (Visible on screens < 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-100 bg-white">
          <div className="px-2 pt-2 pb-4 space-y-1 sm:px-3">
            {menuItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-md text-base font-medium transition-colors cursor-pointer ${
                  isActive(item.href)
                    ? 'bg-amber-50 text-brand-gold font-semibold'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-brand-navy'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
